import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));
const METER_LABEL = { electricity: "Electricity", gas: "Gas", water: "Water" };

export function RecurringIncomeEditor({ value = [], onChange }) {
  const set = (i, patch) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="pt-4 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <Label className="ns-label">Recurring monthly income</Label>
        <button type="button" data-testid="properties-income-add" className="inline-flex items-center gap-1 text-[11px] text-teal-700 hover:text-teal-900 transition-colors"
          onClick={() => onChange([...value, { id: newId(), label: "", monthly_amount: 0, start_month: new Date().toISOString().slice(0, 7), end_month: "" }])}>
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      {value.length === 0 && <div className="text-xs text-slate-400 italic">e.g. contractor rent paid monthly. Channel bookings with a price are counted automatically.</div>}
      <div className="space-y-2">
        {value.map((r, i) => (
          <div key={r.id || i} className="grid grid-cols-[1.4fr_0.8fr_0.9fr_0.9fr_auto] gap-2 items-end" data-testid={`properties-income-row-${i}`}>
            <div><span className="text-[10px] text-slate-500">Label</span>
              <Input className="mt-1 h-9 bg-white text-xs" value={r.label} placeholder="Contractor rent" data-testid={`properties-income-label-${i}`} onChange={(e) => set(i, { label: e.target.value })} /></div>
            <div><span className="text-[10px] text-slate-500">£ / month</span>
              <Input type="number" step="0.01" className="mt-1 h-9 bg-white text-xs" value={r.monthly_amount} data-testid={`properties-income-amount-${i}`} onChange={(e) => set(i, { monthly_amount: e.target.value === "" ? 0 : parseFloat(e.target.value) })} /></div>
            <div><span className="text-[10px] text-slate-500">From</span>
              <Input type="month" className="mt-1 h-9 bg-white text-xs" value={r.start_month || ""} data-testid={`properties-income-start-${i}`} onChange={(e) => set(i, { start_month: e.target.value })} /></div>
            <div><span className="text-[10px] text-slate-500">Until (optional)</span>
              <Input type="month" className="mt-1 h-9 bg-white text-xs" value={r.end_month || ""} data-testid={`properties-income-end-${i}`} onChange={(e) => set(i, { end_month: e.target.value })} /></div>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`properties-income-remove-${i}`} className="text-slate-300 hover:text-red-600 transition-colors mb-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const spikeText = (s) => `${s.month} +${s.pct_over}%${s.est_extra_cost ? ` ≈ £${s.est_extra_cost.toFixed(2)} extra` : ""}`;

export function MeterUsagePanel({ propertyId }) {
  const [usage, setUsage] = useState({});
  const [readings, setReadings] = useState([]);
  const [draft, setDraft] = useState({ meter: "electricity", reading_date: new Date().toISOString().slice(0, 10), value: "", unit: "kWh" });
  const load = () => {
    api.get(`/properties/${propertyId}/meter-usage`).then((r) => setUsage(r.data || {})).catch(() => {});
    api.get(`/properties/${propertyId}/meter-readings`).then((r) => setReadings(r.data || [])).catch(() => {});
  };
  useEffect(() => { if (propertyId) load(); /* eslint-disable-next-line */ }, [propertyId]);
  if (!propertyId) return null;
  const add = async () => {
    try {
      await api.post(`/properties/${propertyId}/meter-readings`, { ...draft, value: parseFloat(draft.value) });
      setDraft({ ...draft, value: "" });
      toast.success("Reading saved");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const del = async (id) => { try { await api.delete(`/properties/${propertyId}/meter-readings/${id}`); load(); } catch (err) { toast.error(apiError(err)); } };
  return (
    <div className="pt-4 border-t border-slate-100" data-testid="properties-meter-panel">
      <Label className="ns-label">Meter usage</Label>
      {Object.keys(usage).length === 0 && <div className="text-xs text-slate-400 italic mt-1">Add two or more readings per meter to see monthly usage.</div>}
      {Object.entries(usage).map(([meter, u]) => <MeterChart key={meter} meter={meter} unit={u.unit} series={u.series} rate={u.rate} basis={u.rate_basis} />)}
      <div className="grid grid-cols-[0.9fr_1fr_0.8fr_0.6fr_auto] gap-2 items-end mt-3">
        <select className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs" value={draft.meter} data-testid="properties-meter-type"
          onChange={(e) => setDraft({ ...draft, meter: e.target.value, unit: e.target.value === "water" ? "m³" : "kWh" })}>
          {Object.entries(METER_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Input type="date" className="h-9 bg-white text-xs" value={draft.reading_date} data-testid="properties-meter-date" onChange={(e) => setDraft({ ...draft, reading_date: e.target.value })} />
        <Input type="number" step="0.01" className="h-9 bg-white text-xs" placeholder="Reading" value={draft.value} data-testid="properties-meter-value" onChange={(e) => setDraft({ ...draft, value: e.target.value })} />
        <Input className="h-9 bg-white text-xs" value={draft.unit} data-testid="properties-meter-unit" onChange={(e) => setDraft({ ...draft, unit: e.target.value })} />
        <button type="button" onClick={add} disabled={draft.value === ""} data-testid="properties-meter-add" className="h-9 px-3 rounded-full bg-teal-700 text-white text-xs hover:bg-teal-800 transition-colors disabled:opacity-50">Add reading</button>
      </div>
      {readings.length > 0 && (
        <ul className="mt-2 max-h-32 overflow-y-auto text-[11px] text-slate-600" data-testid="properties-meter-readings">
          {readings.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-0.5 border-b border-slate-50">
              <span>{r.reading_date} · {METER_LABEL[r.meter]} · <span className="font-mono">{r.value}</span> {r.unit} {r.source === "pod_secure" && <span className="text-teal-700">· pod-secure</span>}</span>
              {r.source === "manual" && <button type="button" onClick={() => del(r.id)} data-testid={`properties-meter-remove-${r.id}`} className="text-slate-300 hover:text-red-600 transition-colors"><Trash2 className="w-3 h-3" /></button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MeterChart({ meter, unit, series, rate, basis }) {
  const spikes = series.filter((s) => s.spike);
  return (
    <div className="mt-2 rounded-xl border border-slate-100 p-3" data-testid={`properties-meter-chart-${meter}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium">{METER_LABEL[meter]} <span className="text-slate-400">({unit || "units"} / month)</span></span>
        {spikes.length > 0 && (
          <span className="inline-flex items-center gap-1 text-rose-700" data-testid={`properties-meter-spikes-${meter}`}>
            <AlertTriangle className="w-3.5 h-3.5" /> {spikes.map(spikeText).join(", ")}
          </span>
        )}
      </div>
      <div className="text-[10px] text-slate-400 mb-1" data-testid={`properties-meter-rate-${meter}`}>
        {rate ? `≈ £${rate.toFixed(3)} per ${unit || "unit"} (from latest ${basis === "account" ? "account cost" : basis})` : "Add a bill or monthly cost to estimate spike costs"}
      </div>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => [`${v} ${unit}`, "Usage"]} />
            <Bar dataKey="usage" radius={[4, 4, 0, 0]}>
              {series.map((s) => <Cell key={s.month} fill={s.spike ? "#e11d48" : "#0f766e"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
